<!-- src/components/layout/PaginationBar.vue -->
<template>
    <!-- Pagination Info & Controls -->
    <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
        <!-- Pagination Info -->
        <div class="text-sm text-slate-500 dark:text-slate-200 order-2 sm:order-1">
            Showing
            <span class="font-semibold text-slate-700 dark:text-slate-300">
                {{ (currentPage - 1) * perPage + 1 }}
            </span>
            to
            <span class="font-semibold text-slate-700 dark:text-slate-300">
                {{ Math.min(currentPage * perPage, total) }}
            </span>
            of
            <span class="font-semibold text-slate-700 dark:text-slate-300">
                {{ total }}
            </span>
            <span class="hidden xs:inline"> results</span>
        </div>

        <!-- Pagination Controls -->
        <div
            class="flex items-center space-x-2 order-1 sm:order-2 w-full sm:w-auto justify-center sm:justify-end">
            <!-- Previous Button -->
            <button @click="goToPage(currentPage - 1)" :disabled="currentPage === 1"
                class="px-4 py-2 text-sm font-medium rounded-lg border transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-slate-700 dark:text-slate-300
                hover:bg-primary/5 dark:hover:bg-primary-dark/5 hover:border-primary dark:hover:border-primary-dark"
                :class="currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-sm'">
                <span class="hidden sm:inline">Previous</span>
                <ChevronLeft class="h-5 w-5 sm:hidden" />
            </button>

            <!-- Page Numbers (Desktop) -->
            <div v-if="totalPages > 1" class="hidden md:flex items-center space-x-1">
                <template v-for="page in visiblePages" :key="page">
                    <button v-if="page !== '...'" @click="goToPage(page as number)"
                        class="px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 min-w-[2.5rem]"
                        :class="page === currentPage
                            ? 'bg-primary text-white dark:bg-primary-dark dark:text-white shadow-md'
                            : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-slate-700 dark:text-slate-300 hover:bg-primary/5 dark:hover:bg-primary-dark/5 hover:border-primary dark:hover:border-primary-dark'">
                        {{ page }}
                    </button>
                    <span v-else
                        class="px-2 text-secondary-text/50 dark:text-secondary-dark-text/50">...</span>
                </template>
            </div>

            <!-- Mobile Page Indicator -->
            <div
                class="md:hidden px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-primary/10 dark:bg-primary-dark/10 rounded-lg">
                {{ currentPage }} / {{ totalPages || 1 }}
            </div>

            <!-- Next Button -->
            <button @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages"
                class="px-4 py-2 text-sm font-medium rounded-lg border transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-slate-700 dark:text-slate-300
                hover:bg-primary/5 dark:hover:bg-primary-dark/5 hover:border-primary dark:hover:border-primary-dark"
                :class="currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-sm'">
                <span class="hidden sm:inline">Next</span>
                <ChevronRight class="h-5 w-5 sm:hidden" />
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'

const props = defineProps<{
    currentPage: number
    totalPages: number
    total: number
    perPage: number
}>()

const emit = defineEmits<{
    (e: 'update:page', value: number): void
    (e: 'page-change', value: number): void
}>()

const goToPage = (page: number) => {
    if (page < 1 || page > props.totalPages) return
    emit('update:page', page)
    emit('page-change', page)
}

const visiblePages = computed(() => {
    const pages: (number | string)[] = []
    const totalP = props.totalPages
    const current = props.currentPage

    if (totalP <= 1) return totalP === 1 ? [1] : []

    if (totalP <= 7) {
        for (let i = 1; i <= totalP; i++) pages.push(i)
    } else {
        pages.push(1)
        if (current <= 3) {
            for (let i = 2; i <= 4; i++) pages.push(i)
            pages.push('...')
            pages.push(totalP)
        } else if (current >= totalP - 2) {
            pages.push('...')
            for (let i = totalP - 3; i <= totalP; i++) pages.push(i)
        } else {
            pages.push('...')
            for (let i = current - 1; i <= current + 1; i++) pages.push(i)
            pages.push('...')
            pages.push(totalP)
        }
    }

    return pages
})
</script>

